import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { Spin } from 'antd';

/**
 * SmartRedirect component that routes users to the first page they have access to
 * based on their permissions. Falls back to /home if no specific pages are available.
 * 
 * Priority order:
 * 1. Orders (orders.actions.view)
 * 2. Inventory (inventory.actions.view)
 * 3. Reports (reports.actions.view)
 * 4. Merchants (merchants.actions.view)
 * 5. Users (users.actions.manageUsers)
 * 6. Messages (system.messages.view)
 * 7. Holidays (system.holidayCalendar.view)
 * 8. Home (no permission required)
 */
export function SmartRedirect() {
  const { hasPermission, loading, error } = usePermissions();

  // Wait for permissions to load
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // If permissions query fails, fallback to home page
  // This prevents getting stuck if the GraphQL schema has issues
  if (error) {
    console.error('Error loading permissions for redirect:', error);
    return <Navigate to="/home" replace />;
  }

  // Check permissions in priority order
  if (hasPermission('orders.actions.view')) {
    return <Navigate to="/orders" replace />;
  }

  if (hasPermission('inventory.actions.view')) {
    return <Navigate to="/inventory" replace />;
  }

  if (hasPermission('reports.actions.view')) {
    return <Navigate to="/reports" replace />;
  }

  if (hasPermission('merchants.actions.view')) {
    return <Navigate to="/merchants" replace />;
  }

  if (hasPermission('users.actions.manageUsers')) {
    return <Navigate to="/system/users" replace />;
  }

  if (hasPermission('system.messages.view')) {
    return <Navigate to="/system/messages" replace />;
  }

  if (hasPermission('system.holidayCalendar.view')) {
    return <Navigate to="/system/holiday-calendar" replace />;
  }

  // Fallback to home page (no permission required)
  return <Navigate to="/home" replace />;
}
